<template>
	<section class="gallery_module" :style="cssVars">
		<div class="container">
			<div
				class="gallery_module__inner"
				:class="{ flip_horizontally: flip_horizontally }"
			>
				<div class="cta_container">
					<primary-button
						v-if="parsedPrimaryButton"
						:link="parsedPrimaryButton.url ? parsedPrimaryButton : false"
						:label="
							parsedPrimaryButton.title ? parsedPrimaryButton.title : null
						"
						:theme="'normal'"
					></primary-button>

					<div class="slider_navigation">
						<div :class="'swiper-button-prev' + ' swiper-prev-' + section_id">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M15 18L9 12L15 6"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</div>
						<div :class="'swiper-button-next ' + ' swiper-next-' + section_id">
							<svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9 18L15 12L9 6"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</div>
					</div>
				</div>

				<div
					class="gallery_module__inner__slider"
					:class="{ flip_horizontally: flip_horizontally }"
				>
					<div class="swiper-container" :class="'swiper-' + section_id">
						<div class="swiper-wrapper">
							<div
								class="swiper-slide"
								v-for="(image, index) in parsedGalleryImages"
								:key="index"
							>
								<div class="gallery_image">
									<img 
										:src="image.url" 
										:alt="image.alt || image.title"
										@click="openLightbox(index)"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import PrimaryButton from './PrimaryButton.vue';
import Swiper from 'swiper';

export default {
	data() {
		return {
			swiper: null,
			window: {
				width: 0,
				height: 0,
			},
		};
	},

	props: {
		primary_button: {},
		gallery_images: {},
		text_alignment: {},
		flip_horizontally: {},
		background_colour: {},
		text_colour: {},
		section_id: {},
	},

	computed: {
		parsedGalleryImages() {
			console.log('Raw gallery_images:', this.gallery_images);
			if (this.gallery_images) {
				// If it's already an array, return it directly
				if (Array.isArray(this.gallery_images)) {
					return this.gallery_images;
				}
				// If it's a string, try to parse it as JSON
				if (typeof this.gallery_images === 'string') {
					try {
						const parsed = JSON.parse(this.gallery_images);
						console.log('Parsed gallery images:', parsed);
						return Array.isArray(parsed) ? parsed : [];
					} catch (e) {
						console.error('Error parsing gallery images:', e);
						return [];
					}
				}
				// If it's an object but not an array, return empty array
				return [];
			} else {
				return [];
			}
		},

		parsedPrimaryButton() {
			if (this.primary_button) {
				try {
					return JSON.parse(this.primary_button);
				} catch (e) {
					return this.primary_button;
				}
			} else {
				return null;
			}
		},

		cssVars() {
			return {
				'--text-alignment': this.text_alignment
					? this.text_alignment
					: 'center',
				'--white': this.background_colour,
				'--text-colour': this.text_colour,
				'--size': '2 / span 10',
			};
		},
	},

	methods: {
		initSwiper() {
			const swiperEl = this.$el.querySelector('.swiper-' + this.section_id);
			if (swiperEl && this.parsedGalleryImages.length > 0) {
				this.swiper = new Swiper(swiperEl, {
					slidesPerView: 1,
					spaceBetween: 10,
					autoHeight: true,
					loop: false,
					watchSlidesProgress: true,
					watchSlidesVisibility: true,
					navigation: {
						nextEl: '.swiper-next-' + this.section_id,
						prevEl: '.swiper-prev-' + this.section_id,
					},
					breakpoints: {
						768: {
							slidesPerView: 1,
							spaceBetween: 10,
						},
						1024: {
							slidesPerView: 1,
							spaceBetween: 10,
						},
					},
				});
			}
		},

		goNext() {
			if (this.swiper && typeof this.swiper.slideNext === 'function') {
				this.swiper.slideNext();
			}
		},

		goPrev() {
			if (this.swiper && typeof this.swiper.slidePrev === 'function') {
				this.swiper.slidePrev();
			}
		},

		openLightbox(index) {
			// You can implement a lightbox here if needed
			console.log('Open lightbox for image:', index);
		},

		handleResize() {
			this.window.width = window.innerWidth;
			this.window.height = window.innerHeight;
			if (this.swiper && typeof this.swiper.update === 'function') {
				this.swiper.update();
			}
		},
	},

	components: {
		PrimaryButton,
	},

	mounted() {
		this.$nextTick(() => {
			this.initSwiper();
		});
	},

	updated() {
		this.$nextTick(() => {
			if (!this.swiper && this.parsedGalleryImages.length > 0) {
				this.initSwiper();
			}
		});
	},

	created() {
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('load', this.handleResize);
		window.dispatchEvent(new Event('resize'));
		this.handleResize();
	},

	destroyed() {
		window.removeEventListener('resize', this.handleResize);
		if (this.swiper) {
			this.swiper.destroy();
		}
	},
};
</script>

<style lang="scss" scoped>
@import '../../styles/common/variables';

.gallery_module {
	overflow: hidden !important;
	background-color: var(--background-colour);

	@media (max-width: $mobile-xl) {
		max-height: unset;
		overflow-x: hidden;
		padding: 0;
	}

	.container {
		position: relative;
		padding-top: 0;
		padding-bottom: 0;

		@media (max-width: $mobile-xl) {
			margin: 0;
			max-width: 100%;
		}
	}

	&__inner {
		display: grid;
		grid-template-columns: repeat(12, 1fr);
		column-gap: 10px;
		row-gap: 24px;
		grid-column: span 12;
		position: relative;
		overflow: hidden;
		padding: 46px 0;

		@media (max-width: $mobile-xl) {
			border-radius: 0;
			row-gap: 32px;
		}

		.cta_container {
			margin-top: 0;
			display: flex;
			justify-content: flex-end;
			flex-direction: row;
			flex-wrap: wrap;
			gap: 24px;
			min-width: 100%;
			grid-column: 7 / span 6;
			position: relative;
			/* padding-top: 32px;
			border-top: 1px solid var(--white); */

			@media (max-width: $mobile-xl) {
				margin-top: 0;
				text-align: left;
				display: flex;
				flex-wrap: wrap;
				gap: var(--spacer-mobile);
				grid-column: span 4;
				order: 1;
			}

			.slider_navigation {
				position: relative;
				top: 0;
				right: 0;
				z-index: 10;
				display: grid;
				grid-template-columns: 1fr 1fr;
				gap: 10px;
				width: 180px;
				height: 48px;
				justify-content: space-between;

				.swiper-button-prev,
				.swiper-button-next {
					width: 40px;
					height: 46px;
					background: var(--fpe-light-grey);
					border-radius: 30px;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
					transition: 0.2s ease all;
					color: var(--fpe-blue);
					border: 1px solid var(--white);
					position: relative;
					top: 0;
					margin-top: 0;
					left: 0;
					right: 0;
					min-width: 80px;
					/* box-shadow: 0px -0px 0 var(--fpe-blue); */

					&:hover {
						background: var(--white);
						transition: 0.2s ease all;
						color: var(--fpe-blue);
						/* box-shadow: 1px -1px 0 var(--fpe-blue); */

						svg {
							path {
								stroke: var(--fpe-blue);
							}
						}
					}

					svg {
						width: 16px;
						height: 16px;
					}
				}
			}
		}

		@media (max-width: $mobile-xl) {
			grid-template-columns: 1fr 1fr;
			grid-column: 1 / span 4;
		}

		&.flip_horizontally {
			@media (max-width: $mobile-xl) {
				grid-column: 1 / span 4;
				border-radius: 0 0 0 0;
			}
		}

		@media (max-width: $mobile-xl) {
			grid-template-columns: 1fr;
			column-gap: 0;
			grid-column: span 4;
		}



		&__slider {
			grid-column: 1 / span 12;
			opacity: 1;
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			align-items: center;
			box-sizing: border-box;
			border-radius: 0;
			overflow: hidden;
			position: relative;
			z-index: 5;
			min-height: 100%;

			@media (max-width: $mobile-xl) {
				height: unset;
				border-left: unset;
				padding-right: unset;
				order: 2;
			}

			&.flip_horizontally {
				order: 0;
				justify-content: center;
				grid-column: 1 / span 6;
				overflow: hidden;
				position: relative;

				@media (max-width: $mobile-xl) {
					order: 1;
					margin-right: 0;
					min-height: auto;
					padding-left: unset;
				}
			}

			@media (max-width: $mobile-xl) {
				grid-column: 1 / span 7;
				margin-right: 0;
				min-height: auto;
			}

			.swiper-wrapper {
				height: 100% !important;

				.swiper-slide {
					height: 100% !important;
				}
			}

			.swiper-container {
				width: 100%;
				height: 100%;
				padding: 0;

				.swiper-slide {
					height: auto;
          /* overflow: hidden;
					border-radius: 10px; */
				}

				.gallery_image {
					width: 100%;
          aspect-ratio: 16/9;
          position: relative;
					overflow: hidden;
					border-radius: 10px;
					cursor: pointer;
					transition: 0.2s ease all;

					&:hover {
						transform: scale(1.05);
					}

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
						transition: 0.2s ease all;
					}
				}
			}
		}
	}
}
</style> 


// WEBPACK FOOTER //
// resources/assets/scripts/vue/GalleryModule.vue